import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';

interface FeatureCategory {
  title: string;
  features: string[];
}

const DEFAULT_CATEGORIES: FeatureCategory[] = [
  {
    title: '1. CORE',
    features: [
      'Project Listings: 1',
      'Units per Project: 20',
      'Property Types: 1',
      'Builder Profile Page: No',
    ],
  },
  {
    title: '2. DISPLAY',
    features: [
      'Images: 10',
      'YouTube Video: No',
      'Brochure PDF: No',
      'Floor Plans: No',
      '360° Virtual Tour: No',
      'VR Tour Link: No',
    ],
  },
  {
    title: '3. LOCALITY',
    features: [
      'Google Map: No',
      'Exact Pin: No',
      'Nearby Places: No',
      'Search Filters: Basic',
    ],
  },
  {
    title: '4. BOOST',
    features: [
      'Featured Listing: No',
      '7 Days: No',
      '15 Days: No',
      '30 Days: No',
      'Search Ranking Boost: No',
      '“New Launch” Tag: No',
      '“Verified” Badge: No',
    ],
  },
  {
    title: '5. LEAD SYSTEM',
    features: [
      'Inquiry Form: Yes',
      'WhatsApp Button: Yes',
      'Email Alerts: No',
      'Lead Dashboard: Basic',
      'Lead Analytics: No',
    ],
  },
  {
    title: '6. PREMIUM',
    features: [
      'Price Trends (Area Data): No',
      'Similar Property Suggestion: No',
      'Saved Property / Wishlist: No',
      'Builder CRM Panel: Basic',
      'Multi-User Access: No',
    ],
  },
];

export default function AdminAddPackage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [pkg, setPkg] = useState<any>({
    name: '',
    type: 'developer',
    price: '',
    period: 'month',
    tagline: '',
    color: '#3b82f6',
    icon: 'fa-building',
    popular: false,
    categories: JSON.parse(JSON.stringify(DEFAULT_CATEGORIES))
  });

  const handleAddField = (catIdx: number) => {
    const newCats = [...pkg.categories];
    newCats[catIdx].features.push('New Feature: No');
    setPkg({ ...pkg, categories: newCats });
  };

  const handleRemoveField = (catIdx: number, featIdx: number) => {
    const newCats = [...pkg.categories];
    newCats[catIdx].features.splice(featIdx, 1);
    setPkg({ ...pkg, categories: newCats });
  };

  const handleAddCategory = () => {
    setPkg({
      ...pkg,
      categories: [...pkg.categories, { title: 'New Category', features: ['Feature 1: No'] }]
    });
  };

  const handleRemoveCategory = (idx: number) => {
    const newCats = [...pkg.categories];
    newCats.splice(idx, 1);
    setPkg({ ...pkg, categories: newCats });
  };

  const handleCategoryTitleChange = (idx: number, title: string) => {
    const newCats = [...pkg.categories];
    newCats[idx].title = title;
    setPkg({ ...pkg, categories: newCats });
  };

  const handleFeatureTextChange = (catIdx: number, featIdx: number, text: string) => {
    const newCats = [...pkg.categories];
    newCats[catIdx].features[featIdx] = text;
    setPkg({ ...pkg, categories: newCats });
  };

  const handleSubmit = async () => {
    if (!token) return;
    if (!pkg.name.trim()) return toast.error('Package title is required');
    if (!pkg.price.trim()) return toast.error('Package price is required');

    setSaving(true);
    try {
      await api.adminCreatePackage(token, pkg);
      toast.success('Package protocol initialized successfully');
      navigate('/admin-packages');
    } catch (err) {
      toast.error('Failed to create package protocol');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto py-10 px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate('/admin-packages')}
              className="w-12 h-12 rounded-2xl bg-white border border-blue-900/10 flex items-center justify-center text-slate-900 hover:bg-white transition-all active:scale-95 shadow-sm"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <div>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight uppercase italic leading-none mb-2">New Protocol</h2>
              <p className="text-blue-950 font-medium">Provisioning a new subscription node for the ecosystem.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-8 py-4 bg-slate-950 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-900 transition-all shadow-xl shadow-slate-950/20 active:scale-95 disabled:opacity-50"
            >
              {saving ? 'Initializing...' : 'Construct Package'}
            </button>
          </div>
        </div>

        <div className="space-y-8">
          {/* Identity & Basic Settings */}
          <div className="bg-white rounded-2xl p-6 border border-blue-900/10">
            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 border-b border-blue-900/10 pb-3">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-[11px] font-bold text-blue-950 uppercase mb-2 ml-1">Target Role</label>
                <select
                  value={pkg.type}
                  onChange={(e) => setPkg({ ...pkg, type: e.target.value as any })}
                  className="w-full px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-sm font-medium text-slate-900 focus:border-indigo-500 transition-all outline-none appearance-none cursor-pointer"
                >
                  <option value="developer">Developers</option>
                  <option value="broker">Brokers</option>
                  <option value="owner">Owners</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-blue-950 uppercase mb-2 ml-1">Package Title</label>
                <input
                  type="text"
                  value={pkg.name}
                  onChange={(e) => setPkg({ ...pkg, name: e.target.value })}
                  placeholder="e.g. Diamond Elite"
                  className="w-full px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-sm font-medium text-slate-900 focus:border-indigo-500 transition-all outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-blue-950 uppercase mb-2 ml-1">Price</label>
                  <input
                    type="text"
                    value={pkg.price}
                    onChange={(e) => setPkg({ ...pkg, price: e.target.value })}
                    placeholder="19999"
                    className="w-full px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-sm font-medium text-slate-900 focus:border-indigo-500 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-blue-950 uppercase mb-2 ml-1">Cycle</label>
                  <input
                    type="text"
                    value={pkg.period}
                    onChange={(e) => setPkg({ ...pkg, period: e.target.value })}
                    placeholder="month/year"
                    className="w-full px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-sm font-medium text-slate-900 focus:border-indigo-500 transition-all outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Functional Matrices */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between px-2">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest">Subscription Features</h4>
                <button
                  onClick={handleAddCategory}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider hover:bg-indigo-700 transition-all shadow-sm active:scale-95"
                >
                  <i className="fa-solid fa-plus text-[8px]"></i> New Category
                </button>
              </div>

              <div className="space-y-6">
                {pkg.categories.map((cat: any, cIdx: number) => (
                  <div key={cIdx} className="bg-white rounded-2xl p-6 border border-blue-900/10 shadow-sm">
                    <div className="flex items-center justify-between gap-4 mb-8 pb-4 border-b border-blue-900/10">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-950 text-sm">
                          <i className={`fa-solid ${cIdx === 0 ? 'fa-server' : cIdx === 1 ? 'fa-display' : 'fa-list-check'}`}></i>
                        </div>
                        <input
                          type="text"
                          value={cat.title}
                          onChange={(e) => handleCategoryTitleChange(cIdx, e.target.value)}
                          className="bg-transparent border-none text-lg font-bold text-slate-900 focus:ring-0 w-full"
                          placeholder="Category Name"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveCategory(cIdx)}
                        className="w-8 h-8 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center border border-rose-100"
                      >
                        <i className="fa-solid fa-trash-can text-xs"></i>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      {cat.features.map((feat: string, fIdx: number) => {
                        const splitIdx = feat.indexOf(':');
                        const label = splitIdx !== -1 ? feat.substring(0, splitIdx).trim() : feat;
                        const valueText = splitIdx !== -1 ? feat.substring(splitIdx + 1).trim() : '';
                        
                        return (
                          <div key={fIdx} className="group/feat">
                            <label className="block text-[10px] font-bold text-blue-950 uppercase mb-1.5 ml-0.5">Attribute {fIdx + 1}</label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 flex gap-1.5">
                                <input
                                  type="text"
                                  value={label}
                                  onChange={(e) => handleFeatureTextChange(cIdx, fIdx, `${e.target.value}: ${valueText || 'No'}`)}
                                  placeholder="Feature Name"
                                  className="flex-1 px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-xs font-medium text-slate-700 focus:border-indigo-500 transition-all outline-none"
                                />
                                <input
                                  type="text"
                                  value={valueText}
                                  onChange={(e) => handleFeatureTextChange(cIdx, fIdx, `${label}: ${e.target.value}`)}
                                  placeholder="Value"
                                  className="w-24 px-3 py-3 bg-white border border-blue-900/10 rounded-xl text-xs font-bold text-slate-950 focus:border-indigo-500 outline-none text-center"
                                />
                              </div>
                              <button
                                onClick={() => handleRemoveField(cIdx, fIdx)}
                                className="w-9 h-9 rounded-lg bg-white text-blue-950 hover:bg-rose-50 hover:text-rose-500 transition-all flex items-center justify-center border border-blue-900/10"
                              >
                                <i className="fa-solid fa-xmark text-xs"></i>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => handleAddField(cIdx)}
                        className="md:col-span-2 flex items-center justify-center gap-2 py-4 px-6 border-2 border-dashed border-blue-900/10 rounded-2xl hover:border-indigo-200 hover:bg-white transition-all mt-2"
                      >
                        <i className="fa-solid fa-plus text-[10px] text-blue-950"></i>
                        <span className="text-[11px] font-bold text-blue-950 uppercase tracking-wider">Add Feature Row</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar Extras */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-blue-900/10 shadow-sm">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest mb-6 border-b border-blue-900/10 pb-3">Marketing</h4>
                <div className="space-y-5">
                  <div>
                    <label className="block text-[11px] font-bold text-blue-950 uppercase mb-2 ml-1">Marketing Tagline</label>
                    <textarea
                      value={pkg.tagline}
                      onChange={(e) => setPkg({ ...pkg, tagline: e.target.value })}
                      placeholder="Enter a brief description..."
                      className="w-full px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-xs font-medium text-slate-900 outline-none min-h-[120px] focus:border-indigo-500"
                    />
                  </div>

                  <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-blue-900/10">
                    <p className="text-[11px] font-bold text-slate-900 uppercase">Popular Badge</p>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={pkg.popular} onChange={() => setPkg({...pkg, popular: !pkg.popular})} />
                      <div className="w-10 h-5 bg-slate-300 rounded-full peer peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-blue-900/10 shadow-sm">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest mb-6 border-b border-blue-900/10 pb-3">Visuals</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold text-blue-950 uppercase mb-2 ml-1">Color (Hex)</label>
                    <input type="text" value={pkg.color} onChange={(e) => setPkg({ ...pkg, color: e.target.value })} className="w-full px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-xs font-medium" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-blue-950 uppercase mb-2 ml-1">Icon (FA)</label>
                    <input type="text" value={pkg.icon} onChange={(e) => setPkg({ ...pkg, icon: e.target.value })} className="w-full px-4 py-3 bg-white border border-blue-900/10 rounded-xl text-xs font-medium" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}


